import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Heart, Leaf, ShoppingCart } from 'lucide-react';
import QuantityStepper from './QuantityStepper';
import ProductOfferNote from './ProductOfferNote';
import { FAVOURITE_AUTH_REQUIRED, useFavourites } from '../context/FavouritesContext';
import { useToast } from '../context/ToastContext';
import { btnPrimary, btnXs, focusRing, itemCard } from '../styles/ui';
import { productPriceView } from '../utils/offerPresentation';
import ProductImage from './ProductImage';

// Pure presentational card except for favourites, which reads shared context
// directly (rather than taking more props) since every card needs the same
// heart-toggle behaviour and FavouritesProvider already guards guest access.
const ProductCard = ({ product, quantity = 0, onAddToCart, onSetQuantity, variant = 'default' }) => {
  const {
    _id,
    sku,
    displayName,
    thumbnail,
    sellingUom,
    isInStock,
    catchWeight,
  } = product;

  const location = useLocation();
  const navigate = useNavigate();
  const { isFavourite, toggleFavourite } = useFavourites();
  const { showToast } = useToast();
  const favourited = isFavourite(_id);
  // Price, struck price, % off and offer exactly as the backend projected
  // them for this location and channel; see utils/offerPresentation.
  const { price, compareAtPrice, discountPercentage } = productPriceView(product);

  const handleToggleFavourite = async () => {
    try {
      const wasFavourited = favourited;
      const result = await toggleFavourite(_id, product);
      if (result === FAVOURITE_AUTH_REQUIRED) {
        const redirect = `${location.pathname}${location.search}${location.hash}`;
        navigate(`/login?redirect=${encodeURIComponent(redirect)}`);
        return;
      }
      showToast(wasFavourited ? 'Removed from favourites' : 'Added to favourites');
    } catch {
      // Best-effort — FavouritesContext already rolls back the optimistic
      // update on failure, nothing further to surface on a product card.
    }
  };

  const handleAddToCart = () => {
    onAddToCart(product);
    showToast(`${displayName} added to cart`);
  };

  // The homepage "Today's Fresh Picks" tile. One fixed geometry for every
  // product, whatever its data: a fixed-height image (badges and the heart
  // overlay it, so they never move anything), a two-line name zone, a unit
  // row, and a bottom row whose price column and action slot have fixed
  // sizes — so image bottoms, names, units, price baselines, buttons and card
  // bottoms line up across the whole rail.
  if (variant === 'compact') {
    const { offer } = productPriceView(product);
    const href = `/products/${sku || _id}`;
    const unitText = sellingUom || (catchWeight ? 'Weight may vary' : '');
    return (
      <div className="group relative flex flex-col bg-white rounded-2xl border border-[#E4EDE4] shadow-[0_4px_14px_-10px_rgba(15,51,24,0.35)] overflow-hidden h-full transition-shadow hover:shadow-[0_10px_22px_-14px_rgba(15,51,24,0.45)]">
        <Link to={href} className={`block relative h-[112px] shrink-0 bg-[#F4F7F3] overflow-hidden isolate ${focusRing}`} aria-label={displayName}>
          <ProductImage
            src={thumbnail}
            alt=""
            size="thumb"
            imgClassName={`transition-transform duration-300 group-hover:scale-105 ${isInStock ? '' : 'opacity-70'}`}
          />
          <span className="absolute top-2 left-2 flex flex-col items-start gap-1 pointer-events-none">
            {discountPercentage && (
              <span className="bg-[#E8452C] text-white text-[10px] font-bold px-1.5 py-[3px] rounded-md leading-none">
                {discountPercentage}% OFF
              </span>
            )}
            {offer && (
              <span className="max-w-[118px] truncate bg-white/95 text-accent-700 text-[9.5px] font-semibold px-1.5 py-[3px] rounded-md leading-none" data-testid="product-offer">
                {offer.label}
              </span>
            )}
          </span>
          {!isInStock && (
            <span className="absolute left-2 bottom-2 bg-white/95 text-[#B42318] border border-[#F4D4D0] text-[10px] font-bold px-2 py-[3px] rounded-full leading-none pointer-events-none">
              Out of stock
            </span>
          )}
        </Link>
        <button
          type="button"
          onClick={handleToggleFavourite}
          aria-label={favourited ? 'Remove from favourites' : 'Add to favourites'}
          aria-pressed={favourited}
          className={`absolute z-10 top-2 right-2 w-7 h-7 rounded-full bg-white/95 shadow-soft flex items-center justify-center transition-transform hover:scale-110 ${focusRing}`}
        >
          <Heart size={13} className={favourited ? 'text-accent-500 fill-accent-500' : 'text-gray-500'} aria-hidden="true" />
        </button>

        <div className="flex flex-col flex-1 px-3 pt-2.5 pb-3">
          <Link to={href} className={`block h-[32px] ${focusRing}`}>
            <h3 className="text-[12.5px] font-bold text-emerald-950 leading-4 line-clamp-2" title={displayName}>{displayName}</h3>
          </Link>
          <span className="mt-1 h-[14px] text-[11px] text-ink-secondary leading-[14px] truncate">{unitText}</span>
          <div className="mt-auto pt-2 grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2">
            {/* Price on its own fixed line; the MRP sits on a second fixed
                line, so its absence never moves the selling price. */}
            <span className="flex flex-col min-w-0">
              <span className="h-[18px] text-[15px] font-extrabold text-emerald-950 leading-[18px] truncate">₹{price ?? '—'}</span>
              <span className="h-[13px] text-[10.5px] text-gray-400 leading-[13px] truncate">
                {compareAtPrice !== null ? <s>₹{compareAtPrice}</s> : null}
              </span>
            </span>
            {/* Fixed action slot: Add, the stepper, or the disabled state. */}
            <span className="w-[74px] h-[30px] flex items-center justify-end">
              {isInStock ? (
                quantity > 0 ? (
                  <QuantityStepper size="xs" quantity={quantity} onChange={(qty) => onSetQuantity(_id, qty)} />
                ) : (
                  <button
                    type="button"
                    onClick={handleAddToCart}
                    className={`w-full h-[30px] inline-flex items-center justify-center gap-1 rounded-full border border-[#2E8B47] text-[#1E5A30] text-[11.5px] font-bold hover:bg-[#2E8B47] hover:text-white transition-colors ${focusRing}`}
                  >
                    <ShoppingCart size={12} strokeWidth={2.5} aria-hidden="true" />
                    Add
                  </button>
                )
              ) : (
                <span className="w-full h-[30px] inline-flex items-center justify-center rounded-full border border-[#E3E8E3] bg-[#F5F7F5] text-[#8A948D] text-[11px] font-semibold" aria-hidden="true">
                  Sold out
                </span>
              )}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`card-hover ${itemCard} overflow-hidden flex flex-col`}>
      <div className="aspect-square bg-gradient-to-br from-emerald-50 to-emerald-100/50 relative overflow-hidden">
        <Link to={`/products/${sku || _id}`} className={`absolute inset-0 block ${focusRing}`}>
          <ProductImage src={thumbnail} alt={displayName} imgClassName="transition-transform duration-300 group-hover:scale-110" />
          {!isInStock && (
            <div className="absolute inset-0 bg-white/70 backdrop-blur-[1px] flex items-center justify-center pointer-events-none">
              <span className="bg-gray-800 text-white text-xs font-semibold px-3 py-1 rounded-full">Out of stock</span>
            </div>
          )}
        </Link>

        <div className="absolute top-2 left-2 flex flex-col gap-1.5 items-start pointer-events-none">
          {discountPercentage && (
            <span className="bg-gradient-accent text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-glow">
              {discountPercentage}% OFF
            </span>
          )}
          {isInStock && (
            <span className="inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm text-emerald-700 text-[10px] font-semibold px-2 py-1 rounded-full shadow-soft">
              <Leaf size={10} aria-hidden="true" /> Fresh
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={handleToggleFavourite}
          aria-label={favourited ? 'Remove from favourites' : 'Add to favourites'}
          aria-pressed={favourited}
          className={`absolute z-10 top-2 right-2 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm shadow-soft flex items-center justify-center transition-all duration-200 hover:scale-110 active:scale-95 ${focusRing}`}
        >
          <Heart
            size={16}
            className={favourited ? 'text-accent-500 fill-accent-500' : 'text-gray-400'}
            aria-hidden="true"
          />
        </button>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <Link to={`/products/${sku || _id}`} className={focusRing}>
          <h3 className="font-bold text-emerald-950 text-sm leading-tight mb-1 line-clamp-2">{displayName}</h3>
        </Link>
        {catchWeight && <p className="text-xs text-accent-600 mb-1">Weight may vary — Pay on Delivery only</p>}
        <ProductOfferNote product={product} className="mb-1.5" />

        <div className="mt-auto pt-2.5 border-t border-emerald-50 flex items-center justify-between gap-2">
          <div>
            <div className="flex items-baseline gap-1.5">
              <span className="font-bold text-emerald-950">₹{price ?? '—'}</span>
              {compareAtPrice !== null && (
                <span className="text-xs text-gray-400 line-through">₹{compareAtPrice}</span>
              )}
            </div>
            {sellingUom && (
              <span className="inline-block mt-0.5 text-[11px] font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-md">
                {sellingUom}
              </span>
            )}
          </div>

          {isInStock &&
            (quantity > 0 ? (
              <QuantityStepper quantity={quantity} onChange={(qty) => onSetQuantity(_id, qty)} />
            ) : (
              <button
                type="button"
                onClick={handleAddToCart}
                className={`${btnPrimary} ${btnXs}`}
              >
                Add
              </button>
            ))}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
